'use client';

import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, DotFilledIcon } from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Icon } from '../icon';
import { Badge } from '../badge';
import { LoadingState } from '../loading-state';

export interface StatCardProps extends BoxProps {
  /** Stat label/title */
  label: string;
  /** Stat value */
  value: string | number;
  /** Optional description */
  description?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Icon background color */
  iconBg?: string;
  /** Icon color */
  iconColor?: string;
  /** Change value */
  change?: {
    value: number;
    label?: string;
    type?: 'increase' | 'decrease' | 'neutral';
  };
  /** Status indicator */
  status?: {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info';
  };
  /** Additional info items */
  info?: Array<{
    label: string;
    value: string | number;
  }>;
  /** Card orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Loading state */
  loading?: boolean;
}

/**
 * StatCard component - displays statistics with rich formatting options
 * Supports icons, trends, status badges, and additional info
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  description,
  icon,
  iconBg = 'bg.primary.muted',
  iconColor = 'fg.primary',
  change,
  status,
  info,
  orientation = 'vertical',
  loading = false,
  ...boxProps
}) => {
  const isHorizontal = orientation === 'horizontal';

  // Get change icon and color
  const getChangeIcon = () => {
    if (!change) return null;
    switch (
      change.type ||
      (change.value > 0 ? 'increase' : change.value < 0 ? 'decrease' : 'neutral')
    ) {
      case 'increase':
        return ArrowUpIcon;
      case 'decrease':
        return ArrowDownIcon;
      default:
        return DotFilledIcon;
    }
  };

  const getChangeColor = () => {
    if (!change) return 'fg.muted';
    const type =
      change.type || (change.value > 0 ? 'increase' : change.value < 0 ? 'decrease' : 'neutral');
    switch (type) {
      case 'increase':
        return 'fg.success';
      case 'decrease':
        return 'fg.error';
      default:
        return 'fg.muted';
    }
  };

  const ChangeIcon = getChangeIcon();
  const changeColor = getChangeColor();

  return (
    <Box
      position="relative"
      padding="20px"
      backgroundColor="bg.surface"
      borderRadius="lg"
      border="1px solid"
      borderColor="border.muted"
      transition="all 0.2s"
      _hover={{
        borderColor: 'border.default',
        boxShadow: 'sm',
      }}
      {...boxProps}
    >
      {loading && <LoadingState preset="statCard" asOverlay blurBackground />}
      <Box
        display="flex"
        flexDirection={isHorizontal ? 'row' : 'column'}
        gap={isHorizontal ? 4 : 3}
        alignItems={isHorizontal ? 'center' : 'stretch'}
      >
        {/* Icon section */}
        {icon && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={isHorizontal ? 16 : 12}
            height={isHorizontal ? 16 : 12}
            backgroundColor={iconBg}
            color={iconColor}
            borderRadius="lg"
            fontSize={isHorizontal ? '2xl' : 'xl'}
            flexShrink={0}
          >
            {icon}
          </Box>
        )}

        {/* Content section */}
        <Box flex={1}>
          {/* Header with label and status */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="4px"
            gap="8px"
          >
            <Text fontSize="sm" color="fg.muted" fontWeight="medium">
              {label}
            </Text>
            {status && <Badge size="sm">{status.label}</Badge>}
          </Box>

          {/* Value and change */}
          <Box
            display="flex"
            alignItems="baseline"
            gap="8px"
            marginBottom={description || info ? 2 : 0}
          >
            <Text fontSize="2xl" fontWeight="bold" color="fg.default" lineClamp={1}>
              {value}
            </Text>
            {change && ChangeIcon && (
              <Box display="flex" alignItems="center" gap={1}>
                <Icon color={changeColor} size="sm">
                  <ChangeIcon />
                </Icon>
                <Text fontSize="sm" color={changeColor} fontWeight="medium">
                  {Math.abs(change.value)}%{change.label && ` ${change.label}`}
                </Text>
              </Box>
            )}
          </Box>

          {/* Description */}
          {description && (
            <Text fontSize="sm" color="fg.muted" marginBottom={info ? 2 : 0} lineClamp={2}>
              {description}
            </Text>
          )}

          {/* Additional info */}
          {info && info.length > 0 && (
            <Box>
              {info.map((item, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  paddingY={1}
                  borderTop={index === 0 ? '1px solid' : undefined}
                  borderTopColor={index === 0 ? 'border.muted' : undefined}
                >
                  <Text fontSize="xs" color="fg.muted">
                    {item.label}
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" color="fg.default">
                    {item.value}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default StatCard;
